import { useMemo } from 'react';
import { Subtitle } from '../../components/shared';
import { IFarmProperty, ILineProperty } from '../../entities/farms.entities';
import {
  IOysterHarvest,
  IOysterSeeding,
} from '../../entities/growing.entities';
import { formatNumber } from '../../entities/util-functions';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { displayValueUnit, pcsToDoz } from '../../lib/common.helpers';
import { SelectAccountSetting } from '../../store/extra/extra.selector';
import { getMortalityLabel } from '../../lib/farm.helpers';

interface Props {
  farm: IFarmProperty;
  line: ILineProperty;
  seedings: IOysterSeeding[];
  harvests: IOysterHarvest[];
  harvestID: number;
  stageAmountSum: number;
  stageMortalitySum: number;
}

const OysterHarvestHeader = ({
  farm,
  line,
  seedings,
  harvests,
  harvestID,
  stageAmountSum,
  stageMortalitySum,
}: Props) => {
  const lang = useSelector(selectLang);
  const accSetting = SelectAccountSetting(farm.id);

  const { seededBaskets, harvest } = useMemo(() => {
    const seededBaskets = seedings.reduce((p, c) => p + c.basket_count, 0);
    const harvest = harvests.find(x => x.id === harvestID);

    return { seededBaskets, harvest };
  }, [seedings, harvests, harvestID]);

  return (
    <div className='d-flex justify-between white-card pt-28 pr-16 pb-28 pl-24 mb-16 overflow-auto'>
      <div>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4'
        >
          {translate(lang, 'Farm name')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {farm.name}
        </Subtitle>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4 mt-28'
        >
          {translate(lang, 'Line name')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {line.line_name}
        </Subtitle>
      </div>
      <div>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4'
        >
          {translate(lang, 'Farm number')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {farm.farm_number}
        </Subtitle>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4 mt-28'
        >
          {translate(lang, 'Number of %s seeded', line.farming_method)}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {seededBaskets}
        </Subtitle>
      </div>
      <div>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4'
        >
          {translate(lang, 'Farm area')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {displayValueUnit(
            farm.area,
            accSetting?.display_unit,
            'area_hectare',
            '-',
          )}
        </Subtitle>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4 mt-28'
        >
          {translate(lang, 'Line %s', line.farming_method)}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {`${line.basket_count} ${line.farming_method}`}
        </Subtitle>
      </div>
      <div>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4'
        >
          {translate(lang, 'This harvest amount')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {`${formatNumber(harvest?.amount_dz ?? 0)} dozens`}
        </Subtitle>
        <Subtitle
          size={3}
          color='black'
          align='left'
          fontWeight={400}
          className='mb-4 mt-28'
        >
          {translate(lang, '_total_harvested_stage')}
        </Subtitle>
        <Subtitle size={4} color='black' align='left' fontWeight={500}>
          {`${formatNumber(pcsToDoz(stageAmountSum))} dozens`}
        </Subtitle>
      </div>
      {!!accSetting?.track_mortality && (
        <div>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4'
          >
            {translate(lang, '_harvest_mortality')}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {harvest ? getMortalityLabel(harvest, 'dozens') : ''}
          </Subtitle>
          <Subtitle
            size={3}
            color='black'
            align='left'
            fontWeight={400}
            className='mb-4 mt-28'
          >
            {translate(lang, '_mortality_stage')}
          </Subtitle>
          <Subtitle size={4} color='black' align='left' fontWeight={500}>
            {!!stageAmountSum
              ? `${formatNumber(
                  pcsToDoz(stageMortalitySum),
                )} dozens (${formatNumber(
                  (stageMortalitySum / stageAmountSum) * 100,
                )} %)`
              : '-'}
          </Subtitle>
        </div>
      )}
    </div>
  );
};

export default OysterHarvestHeader;
